import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { createApiRoot, attachUrugandaInterceptors } from '@uruganda/http';
import { resolveRuntimeBackendUrl } from '../services/api';
import { clearAgoraInitiativeCache } from '../utils/agoraInitiative';
import { resolveLoginPath } from '../utils/portalAdminNav';
import { parseIamLoginFailure } from '../utils/iamLoginError';

const AuthContext = createContext(null);

/** IAM plateforme : `/api/v1/iam/*` — pas sous `/api/apps/entrea` (évite la concat axios → …/api/v1/… en double). */
const IAM_API_ROOT = createApiRoot(resolveRuntimeBackendUrl());
const iamHttp = axios.create({ baseURL: IAM_API_ROOT });
attachUrugandaInterceptors(iamHttp, {
  includeOrgHeaders: false,
  clearProjectOn401: false,
  loginPath: () =>
    typeof window !== 'undefined' ? resolveLoginPath(window.location.pathname) : '/login',
});
const APP_ID = (process.env.REACT_APP_APP_ID || 'entrea').trim().toLowerCase();
const MOBILE_PIN_KEY = 'entrea_mobile_pin_v1';
const MOBILE_PIN_UNLOCKED_KEY = 'entrea_mobile_pin_unlocked';

function readMobilePinData() {
  try {
    const raw = localStorage.getItem(MOBILE_PIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await iamHttp.get('/v1/iam/me');
          setUser(response.data?.user || null);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('project_id');
          localStorage.removeItem('organization_id');
          localStorage.removeItem('current_org_id');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await iamHttp.post('/v1/iam/login', {
        username,
        password,
        audience: 'organization',
        app_id: APP_ID,
      });
      
      const { access_token, user } = response.data;
      if (!user?.project_id) {
        return {
          success: false,
          error: "Aucun projet actif pour cette application. Accès refusé.",
        };
      }
      
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      localStorage.setItem('access_token', access_token);
      if (user.organization_id) {
        localStorage.setItem('organization_id', user.organization_id);
        localStorage.setItem('current_org_id', user.organization_id);
      }
      localStorage.setItem('project_id', user.project_id);
      try {
        const pinData = readMobilePinData();
        const uid = String(user?.id || user?.sub || '').trim();
        const hasLocalPin =
          Boolean(uid) &&
          pinData &&
          String(pinData.user_id || '').trim() === uid &&
          String(pinData.pin || '').trim().length >= 4;
        if (!hasLocalPin) {
          sessionStorage.setItem(MOBILE_PIN_UNLOCKED_KEY, '1');
        } else {
          sessionStorage.removeItem(MOBILE_PIN_UNLOCKED_KEY);
        }
      } catch {
        /* ignore */
      }

      return {
        success: true,
        mustChangePassword: !!user?.must_change_password,
        user,
      };
    } catch (error) {
      const parsed = parseIamLoginFailure(error);
      return {
        success: false,
        errorKey: parsed.errorKey,
        error: parsed.message,
      };
    }
  };

  const loginWithToken = (access_token, user) => {
    setToken(access_token);
    setUser(user);
    localStorage.setItem('token', access_token);
    localStorage.setItem('access_token', access_token);
    if (user?.organization_id) {
      localStorage.setItem('organization_id', user.organization_id);
      localStorage.setItem('current_org_id', user.organization_id);
    }
    if (user?.project_id) {
      localStorage.setItem('project_id', user.project_id);
    }
    try {
      const pinData = readMobilePinData();
      const uid = String(user?.id || user?.sub || '').trim();
      const hasLocalPin =
        Boolean(uid) &&
        pinData &&
        String(pinData.user_id || '').trim() === uid &&
        String(pinData.pin || '').trim().length >= 4;
      if (!hasLocalPin) {
        sessionStorage.setItem(MOBILE_PIN_UNLOCKED_KEY, '1');
      } else {
        sessionStorage.removeItem(MOBILE_PIN_UNLOCKED_KEY);
      }
    } catch {
      /* ignore */
    }
  };

  const register = async (name, email, password, role = 'participant') => {
    return {
      success: false,
      error: "Inscription directe non supportée dans ce mode. Utilisez un compte IAM organisationnel.",
    };
  };

  const logout = async () => {
    try {
      if (token) {
        await iamHttp.post('/v1/iam/logout');
      }
    } catch (_err) {
      // Déconnexion locale même si la déconnexion serveur échoue.
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('project_id');
    localStorage.removeItem('organization_id');
    localStorage.removeItem('current_org_id');
    localStorage.removeItem(MOBILE_PIN_KEY);
    try {
      sessionStorage.removeItem(MOBILE_PIN_UNLOCKED_KEY);
    } catch {
      /* ignore */
    }
    clearAgoraInitiativeCache();
  };

  const hasMobilePinForUserId = (userId) => {
    const pinData = readMobilePinData();
    const uid = String(userId || '').trim();
    if (!pinData || !uid) return false;
    return String(pinData.user_id || '').trim() === uid && String(pinData.pin || '').trim().length >= 4;
  };

  const hasMobilePinForCurrentUser = () => hasMobilePinForUserId(user?.id || user?.sub);

  const isMobilePinUnlocked = () => {
    try {
      return sessionStorage.getItem(MOBILE_PIN_UNLOCKED_KEY) === '1';
    } catch {
      return false;
    }
  };

  const isMobilePinRequired = () => hasMobilePinForCurrentUser() && !isMobilePinUnlocked();

  const setMobilePin = (pin, forUserId) => {
    const normalizedPin = String(pin || '').trim();
    const uid = String(forUserId || user?.id || user?.sub || '').trim();
    if (!uid || normalizedPin.length < 4) return false;
    try {
      localStorage.setItem(
        MOBILE_PIN_KEY,
        JSON.stringify({
          user_id: uid,
          pin: normalizedPin,
          updated_at: new Date().toISOString(),
        })
      );
      sessionStorage.setItem(MOBILE_PIN_UNLOCKED_KEY, '1');
      return true;
    } catch {
      return false;
    }
  };

  const verifyMobilePin = (pin) => {
    const normalizedPin = String(pin || '').trim();
    const pinData = readMobilePinData();
    const uid = String(user?.id || user?.sub || '').trim();
    if (!pinData || !uid) return false;
    if (String(pinData.user_id || '').trim() !== uid) return false;
    if (String(pinData.pin || '') !== normalizedPin) return false;
    try {
      sessionStorage.setItem(MOBILE_PIN_UNLOCKED_KEY, '1');
    } catch {
      /* ignore */
    }
    return true;
  };

  const value = {
    user,
    token,
    login,
    loginWithToken,
    register,
    logout,
    loading,
    isAuthenticated: !!token && !!localStorage.getItem('project_id'),
    mustChangePassword: !!user?.must_change_password,
    hasMobilePinForCurrentUser,
    hasMobilePinForUserId,
    isMobilePinRequired,
    setMobilePin,
    verifyMobilePin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
